import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, ChevronLeft, ChevronRight, User, Download } from 'lucide-react';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaveReport = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLeaveCalendar();
  }, [currentDate]);

  const fetchLeaveCalendar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const response = await fetch(
        `${API_URL}/api/leave-management/leave-calendar?month=${month}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
      }
    } catch (error) {
      console.error('Error fetching leave calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDay };
  };

  const isLeaveDay = (day) => {
    const checkDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    ).toISOString().split('T')[0];

    return applications.filter(app => {
      const fromDate = new Date(app.from_date);
      const toDate = new Date(app.to_date);
      const currentCheckDate = new Date(checkDate);
      return currentCheckDate >= fromDate && currentCheckDate <= toDate;
    });
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const { daysInMonth, firstDay } = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const exportToCSV = () => {
    const csvContent = [
      ['Staff Name', 'Employee ID', 'Leave Type', 'From Date', 'To Date', 'Total Days', 'Status'],
      ...applications.map(app => [
        app.staff_name,
        app.employee_id,
        app.leave_type_name,
        app.from_date,
        app.to_date,
        app.total_days,
        app.status
      ])
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-report-${monthName.replace(' ', '-')}.csv`;
    a.click();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Calendar & Reports</h1>
          <p className="text-gray-600 mt-1">View approved leaves and generate reports</p>
        </div>
        <Button onClick={exportToCSV} className="gap-2" variant="outline">
          <Download className="h-4 w-4" />
          Export to CSV
        </Button>
      </div>

      {/* Calendar Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {monthName}
            </CardTitle>
            <div className="flex gap-2">
              <Button onClick={() => changeMonth(-1)} variant="outline" size="sm">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => setCurrentDate(new Date())}
                variant="outline"
                size="sm"
              >
                Today
              </Button>
              <Button onClick={() => changeMonth(1)} variant="outline" size="sm">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading calendar...</div>
          ) : (
            <div className="grid grid-cols-7 gap-2">
              {/* Week Day Headers */}
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="text-center font-semibold text-sm text-gray-600 py-2"
                >
                  {day}
                </div>
              ))}

              {/* Empty cells for days before month starts */}
              {Array.from({ length: firstDay }).map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square" />
              ))}

              {/* Calendar Days */}
              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const leavesOnDay = isLeaveDay(day);
                const hasLeaves = leavesOnDay.length > 0;

                return (
                  <div
                    key={day}
                    className={`aspect-square border rounded-lg p-2 relative group ${
                      hasLeaves ? 'bg-blue-50 border-blue-300' : 'bg-white'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-700">{day}</div>
                    {hasLeaves && (
                      <>
                        <div className="text-xs text-blue-600 font-medium mt-1">
                          {leavesOnDay.length} leave{leavesOnDay.length > 1 ? 's' : ''}
                        </div>
                        {/* Tooltip on hover */}
                        <div className="absolute hidden group-hover:block z-10 bg-white border-2 border-blue-300 rounded-lg p-3 shadow-lg min-w-[200px] top-full left-0 mt-1">
                          {leavesOnDay.map((leave, idx) => (
                            <div key={idx} className="text-xs mb-2 last:mb-0">
                              <div className="font-semibold">{leave.staff_name}</div>
                              <div className="text-gray-600">{leave.leave_type_name}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leave List Card */}
      <Card>
        <CardHeader>
          <CardTitle>Approved Leaves - {monthName}</CardTitle>
        </CardHeader>
        <CardContent>
          {applications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No approved leaves for this month</p>
            </div>
          ) : (
            <div className="space-y-3">
              {applications.map((application) => (
                <div
                  key={application.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-full">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{application.staff_name}</h3>
                        <p className="text-xs text-gray-500">ID: {application.employee_id}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="mb-1">
                        {application.leave_type_name}
                      </Badge>
                      <div className="text-lg font-bold text-blue-600">
                        {application.total_days} day{application.total_days > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                    <div>
                      <span className="text-gray-500">From:</span>
                      <span className="ml-2 font-medium">
                        {formatDate(application.from_date)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">To:</span>
                      <span className="ml-2 font-medium">
                        {formatDate(application.to_date)}
                      </span>
                    </div>
                  </div>

                  {application.reason && (
                    <div className="mt-3 text-sm">
                      <span className="text-gray-500">Reason:</span>
                      <span className="ml-2 text-gray-700">{application.reason}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50">
        <CardHeader>
          <CardTitle className="text-sm">Monthly Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-2xl font-bold text-blue-600">{applications.length}</div>
              <div className="text-sm text-gray-600">Total Approved Leaves</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {new Set(applications.map(a => a.staff_id)).size}
              </div>
              <div className="text-sm text-gray-600">Staff Members on Leave</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {applications.reduce((sum, app) => sum + app.total_days, 0)}
              </div>
              <div className="text-sm text-gray-600">Total Leave Days</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaveReport;